import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faBan, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { AuthGuard } from "../App";
import { StandardURL } from "../App";
import { Oval } from "react-loader-spinner";

// --- Reusable Base Modal Component ---
const BaseModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl">&times;</button>
                </div>
                {children}
            </div>
        </div>
    );
};

// --- Add Blocked IP Modal Component ---
const AddIpModal = ({ isOpen, onClose, onSubmit, formData, handleChange, loading }) => {
    return (
        <BaseModal isOpen={isOpen} onClose={onClose} title="Block New IP Address">
            <form onSubmit={onSubmit}>
                <div className="p-6 grid grid-cols-1 gap-4">
                    <input
                        type="text"
                        name="blockedip"
                        value={formData.blockedip}
                        onChange={handleChange}
                        placeholder="IP Address (e.g., 192.168.1.1)"
                        pattern="^[\w.:-]+$"
                        title="Enter a valid IP address (e.g., 192.168.1.1)"
                        className="border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-red-500 focus:border-red-500"
                        required
                    />
                    <textarea
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                        placeholder="Reason for blocking"
                        rows="3"
                        className="border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-red-500 focus:border-red-500 resize-none"
                        required
                    />
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
                    <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-200">Cancel</button>
                    <button type="submit" disabled={loading} className={`px-5 py-2 rounded-lg text-white font-semibold shadow-md transition duration-200 flex items-center justify-center ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}>
                        {loading ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : <FontAwesomeIcon icon={faBan} className="mr-2" />}
                        Block IP
                    </button>
                </div>
            </form>
        </BaseModal>
    );
};


const BlockIpPage = () => {
    const [blockedIps, setBlockedIps] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingActionId, setLoadingActionId] = useState(null); // Used for specific row delete action
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const initialFormState = {
        blockedip: '',
        reason: '',
    };
    const [formData, setFormData] = useState(initialFormState);

    const token = localStorage.getItem("token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
    };

    useEffect(() => {
        fetchBlockedIps();
    }, []);

    // --- API Calls ---

    // 1. FETCH ALL (GET: /iNyumba/admin/blocked-ip/list)
    const fetchBlockedIps = async () => {
        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/admin/blocked-ip/list`,
                config
            );

            // Assuming response.data.response is the array of IPs
            setBlockedIps(Array.isArray(response.data.response) ? response.data.response : []);
            toast.success("Blocked IP list fetched successfully!");
        } catch (error) {
            console.error("Error fetching blocked IPs:", error);
            toast.error(error.response?.data?.response || "Failed to fetch blocked IPs.");
            setBlockedIps([]);
        } finally {
            setIsLoading(false);
        }
    };

    // 2. ADD NEW IP (POST: /iNyumba/admin/blocked-ip/add)
    const handleAddBlockedIp = async (e) => {
        e.preventDefault();
        
        setLoadingActionId('add'); // Use a placeholder for the modal
        
        try {
            const payload = {
                blockedip: formData.blockedip,
                reason: formData.reason,
            };

            const response = await axios.post(
                `${StandardURL}/iNyumba/admin/blocked-ip/add`,
                payload,
                config
            );
            
            toast.success(response.data.response || `IP ${formData.blockedip} blocked successfully!`);
            setIsAddModalOpen(false);
            setFormData(initialFormState);
            fetchBlockedIps();
        } catch (error) {
            console.error("Error adding blocked IP:", error);
            toast.error(error.response?.data?.response || "Failed to block IP address.");
        } finally {
            setLoadingActionId(null);
        }
    };

    // 3. DELETE IP (DELETE: /iNyumba/admin/blocked-ip/delete/{id})
    const handleDeleteBlockedIp = async (ipRecord) => {
        const id = ipRecord.id; // Assuming the unique identifier is 'id'
        const ipAddress = ipRecord.blockedip;

        if (!window.confirm(`Are you sure you want to unblock the IP address: ${ipAddress}?`)) {
            return;
        }
        
        setLoadingActionId(id);

        try {
            const response = await axios.delete(
                `${StandardURL}/iNyumba/admin/blocked-ip/delete/${id}`,
                config
            );
            
            toast.success(response.data.response || `IP ${ipAddress} unblocked successfully!`);
            fetchBlockedIps();
        } catch (error) {
            console.error("Error deleting blocked IP:", error);
            toast.error(error.response?.data?.response || `Failed to unblock IP ${ipAddress}.`);
        } finally {
            setLoadingActionId(null);
        }
    };

    // --- UI Handlers ---

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    
    const handleOpenAddModal = () => {
        setFormData(initialFormState);
        setIsAddModalOpen(true);
    };

    return (
        <AuthGuard>
            <div className="container mx-auto px-4 py-8">
            
                <h1 className="text-4xl font-extrabold text-gray-900 mb-8 border-b-4 border-red-600 pb-2 flex items-center">
                    <FontAwesomeIcon icon={faBan} className="mr-4 text-red-600" />
                    Blocked IP Management
                </h1>

                {/* Header and Add Button */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-gray-700">Blocked Addresses</h2>
                    <button
                        onClick={handleOpenAddModal}
                        className="flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-red-500/50"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Add Blocked IP
                    </button>
                </div>

                {/* Blocked IPs Table */}
                <div className="bg-white shadow-2xl rounded-xl overflow-hidden">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <Oval height={30} width={30} color="#DC2626" visible={true} ariaLabel="loading-indicator" secondaryColor="#FCA5A5" />
                            <span className="ml-3 text-lg text-gray-600">Loading blocked IP list...</span>
                        </div>
                    ) : blockedIps.length === 0 ? (
                        <div className="text-center py-10 text-gray-500 text-lg">
                            No IP addresses are currently blocked.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {["ID", "IP Address", "Reason", "Actions"].map((header) => (
                                            <th
                                                key={header}
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {blockedIps.map((ipRecord) => (
                                        <tr key={ipRecord.id} className="hover:bg-red-50 transition duration-150">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{ipRecord.id}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-red-600">{ipRecord.blockedip}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600 max-w-sm truncate">{ipRecord.reason}</td>
                                            
                                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                                                <button
                                                    onClick={() => handleDeleteBlockedIp(ipRecord)}
                                                    disabled={loadingActionId === ipRecord.id}
                                                    className="p-2 rounded-full text-green-600 hover:text-green-900 hover:bg-green-50 transition duration-150 disabled:opacity-50"
                                                    title="Unblock IP"
                                                >
                                                    {loadingActionId === ipRecord.id ? (
                                                        <FontAwesomeIcon icon={faSpinner} spin />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add IP Modal */}
            <AddIpModal
                isOpen={isAddModalOpen}
                onClose={() => {
                    setIsAddModalOpen(false);
                    setFormData(initialFormState);
                }}
                onSubmit={handleAddBlockedIp}
                formData={formData}
                handleChange={handleChange}
                loading={loadingActionId === 'add'}
            />

        </AuthGuard>
    );
};

export default BlockIpPage;